import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  input,
  linkedSignal,
  output,
  signal,
  TemplateRef,
  untracked,
  ViewContainerRef,
} from '@angular/core';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';
import { isDefined, isIdentifiable } from '@softline/core';
import { I18nModule } from '../../i18n/i18n.module';
import { UiCorePipesModule } from '../../pipes/ui-core-pipes.module';
import {
  SOFTLINE_MASTER_DETAIL_CONFIG,
  SOFTLINE_MASTER_DETAIL_HEIGHT,
} from '../../ui-core.tokens';
import { BreakpointService } from '../../utilities/breakpoints/services/breakpoint.service';
import { IconComponent } from '../icon/icon.component';

export interface MasterDetailMasterTemplateConfig {
  showIcon: boolean;
  showHover: boolean;
  showSelection: boolean;
}

export const DEFAULT_MASTER_DETAIL_MASTER_TEMPLATE_CONFIG = {
  showIcon: true,
  showHover: true,
  showSelection: true,
};

@Component({
  selector: 'soft-master-detail',
  templateUrl: './master-detail.component.html',
  styleUrls: ['./master-detail.component.css'],
  imports: [CommonModule, UiCorePipesModule, I18nModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MasterDetailComponent<T> {
  private _viewContainerRef = inject(ViewContainerRef);

  isIdentifiable = isIdentifiable;

  #selectedIndex = linkedSignal<T[], number>({
    source: () => this.data(),
    computation: (source, previous) => {
      let previousSelected: T | null = null;

      if (
        previous?.value &&
        previous?.source &&
        previous.value < previous.source.length
      ) {
        previousSelected = previous?.source[previous?.value];
      }

      const isDesktop = untracked(() => !this.breakpointService.isMobile());

      if (source.length <= 0) return -1;

      if (source.length > (previous?.value ?? 0)) {
        const currentSelected = source[previous?.value ?? 0];

        if (isIdentifiable(currentSelected) && isIdentifiable(previousSelected))
          return currentSelected.id === previousSelected?.id
            ? (previous?.value ?? 0)
            : isDesktop
              ? 0
              : -1;
      }

      if (isDesktop && (previous?.value ?? -1 === -1)) return 0;

      return isDesktop ? 0 : -1;
    },
  });

  #providedHeight = inject(SOFTLINE_MASTER_DETAIL_HEIGHT, { optional: true });
  #config = inject(SOFTLINE_MASTER_DETAIL_CONFIG, { optional: true });
  breakpointService = inject(BreakpointService);

  data = input<T[]>([]);

  selectable = input<boolean>(true);

  masterHeaderTemplate = input<TemplateRef<any> | null>(null);
  masterTemplate = input<TemplateRef<any> | null>(null);
  masterBottomTemplate = input<TemplateRef<any> | null>(null);
  masterContainerClass = input<string>();
  masterTemplateConfig = input<MasterDetailMasterTemplateConfig>({
    showIcon: true,
    showHover: true,
    showSelection: true,
  });

  detailTemplate = input<TemplateRef<any> | null>(null);
  detailBottomTemplate = input<TemplateRef<any> | null>(null);
  detailPlaceholder = input<TemplateRef<any> | null>(null);

  detailHeaderVisible = input<boolean>(true);

  masterDblClick = output<T>();

  @Input({ alias: 'selected' })
  set selectedInput(value: T | null) {
    let index = -1;

    if (isIdentifiable(value))
      index = this.data()
        .filter((o): o is T & { id: any } => isIdentifiable(o))
        .findIndex((o) => o.id === value.id);
    else if (isDefined(value)) index = this.data().indexOf(value);

    this.#selectedIndex.set(index);
  }

  selectedIndex = this.#selectedIndex.asReadonly();

  selected = computed<T | null>(() => this.data()[this.#selectedIndex()]);
  selectedChange = outputFromObservable(toObservable(this.selected));

  readonly styles = signal<Partial<CSSStyleDeclaration>>({});

  view = computed<'detail' | 'master' | 'both'>(() => {
    const selectedIndex = this.#selectedIndex();
    const isMobile = this.breakpointService.isMobile();
    if (selectedIndex > -1 && isMobile) return 'detail';
    else if (isMobile) return 'master';
    return 'both';
  });

  constructor() {
    effect(() => {
      const providedHeight = this.#providedHeight;
      const config = this.#config?.();

      if (config && config?.autoHeight === false) {
        this.styles.set({
          minHeight: undefined,
          maxHeight: 'inherit',
        });
        return;
      }

      if (!providedHeight) return;

      const height = providedHeight();

      untracked(() => {
        this.styles.set({
          minHeight: height === 0 || height === null ? 'inherit' : `${height}px`,
          maxHeight: height === 0 || height === null ? 'inherit' : `${height}px`,
        });
      });
    });
  }

  select(index: number): void {
    this.#selectedIndex.set(index);
  }

  // Um beim Pull To refresh Container nicht voreilig neu zu laden (wenn der untere scrollcontainer
  // irgendwo in der mitte steht der vom parent aber ganz oben) darf das touchstart event
  // nicht weitergegeben werden ausser der untere scrollcontainer ist ganz oben.
  preventScrolling(event: TouchEvent, container: HTMLDivElement): void {
    if (container.scrollTop > 0) event.stopPropagation();
  }
}
